import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { motion } from "framer-motion";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { isMobileDevice } from "../../../../../utils/deviceDetection";
import { Package, Truck, FileText, AlertTriangle, Plus, LayoutDashboard, BarChart3, Menu, X } from "lucide-react";
import "./RoldanDashboard.scss";
import { selectAuthState } from "../../../../../store/selectors/authSelectors";
import * as roldanApi from "../../../../../ApiCalls/taktikApiCalls/roldanApi";
import PanelControl from "./components/PanelControl";
import Dashboard from "./components/Dashboard";
import RemesaList from "./components/RemesaList";
import RemesaDetail from "./components/RemesaDetail";
import RemesaForm from "./components/RemesaForm";
import PreManifiestoList from "./components/PreManifiestoList";
import PreManifiestoDetail from "./components/PreManifiestoDetail";
import PreManifiestoForm from "./components/PreManifiestoForm";
import ManifiestoList from "./components/ManifiestoList";
import ManifiestoDetail from "./components/ManifiestoDetail";
import AlertasList from "./components/AlertasList";

const RoldanDashboard = ({
  mantisBusinessId,
  token,
  mantisBusinessUser,
  userId,
  user,
  isLogisticsDemo = false,
}) => {
  const [currentView, setCurrentView] = useState("panel-control"); // panel-control, dashboard, remesas, pre-manifiestos, manifiestos, alertas, de-una
  const [selectedRemesaId, setSelectedRemesaId] = useState(null);
  const [selectedPreManifiestoId, setSelectedPreManifiestoId] = useState(null);
  const [selectedManifiestoId, setSelectedManifiestoId] = useState(null);
  const [showRemesaForm, setShowRemesaForm] = useState(false);
  const [editingRemesaId, setEditingRemesaId] = useState(null);
  const [showPreManifiestoForm, setShowPreManifiestoForm] = useState(false);
  const [editingPreManifiestoId, setEditingPreManifiestoId] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobileDeviceType, setIsMobileDeviceType] = useState(false);

  // Detect device type (not screen size)
  useEffect(() => {
    setIsMobileDeviceType(isMobileDevice());
  }, []);

  // Close mobile menu when modals open
  useEffect(() => {
    if (showRemesaForm || showPreManifiestoForm) {
      setMobileMenuOpen(false);
    }
  }, [showRemesaForm, showPreManifiestoForm]);

  // DeUna section state (now for vehiculos by placa)
  const [placaInput, setPlacaInput] = useState("");
  const [vehiculoData, setVehiculoData] = useState(null);
  const [loadingVehiculo, setLoadingVehiculo] = useState(false);
  const [vehiculoError, setVehiculoError] = useState(null);

  const isAdmin = mantisBusinessUser?.mainUser || false;

  // Navigation handlers
  const handleViewChange = (view) => {
    setCurrentView(view);
    setSelectedRemesaId(null);
    setSelectedPreManifiestoId(null);
    setSelectedManifiestoId(null);
    setShowRemesaForm(false);
    setEditingRemesaId(null);
    setShowPreManifiestoForm(false);
    setEditingPreManifiestoId(null);
    setMobileMenuOpen(false); // Close mobile menu when navigating
  };

  const handleRemesaSelect = (remesaId) => {
    setSelectedRemesaId(remesaId);
    setCurrentView("remesas");
  };

  const handlePreManifiestoSelect = (preManifiestoId) => {
    setSelectedPreManifiestoId(preManifiestoId);
    setCurrentView("pre-manifiestos");
  };

  const handleManifiestoSelect = (manifiestoId) => {
    setSelectedManifiestoId(manifiestoId);
    setCurrentView("manifiestos");
  };

  const handleCreateRemesa = (preManifiestoId = null) => {
    setShowRemesaForm(true);
    setEditingRemesaId(null);
    setCurrentView("remesas");
    // Store pre_manifiesto_id for quick creation
    if (preManifiestoId) {
      window.currentPreManifiestoIdForRemesa = preManifiestoId;
    } else {
      window.currentPreManifiestoIdForRemesa = null;
    }
  };

  const handleEditRemesa = (remesaId) => {
    setEditingRemesaId(remesaId);
    setShowRemesaForm(true);
    setCurrentView("remesas");
  };

  const handleCreatePreManifiesto = () => {
    setShowPreManifiestoForm(true);
    setEditingPreManifiestoId(null);
    setCurrentView("pre-manifiestos");
  };

  const handleCloseForm = () => {
    setShowRemesaForm(false);
    setEditingRemesaId(null);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleClosePreManifiestoForm = () => {
    setShowPreManifiestoForm(false);
    setEditingPreManifiestoId(null);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // Helper function to format keys (convert snake_case to Title Case)
  const formatKey = (key) => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Helper function to format values
  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return "N/A";
    }
    if (typeof value === "boolean") {
      return value ? "Sí" : "No";
    }
    if (typeof value === "number") {
      // Format large numbers with commas
      return value.toLocaleString("es-ES");
    }
    if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}/)) {
      // Format dates
      try {
        const date = new Date(value);
        // Check if date is valid
        if (isNaN(date.getTime())) {
          return value;
        }
        // Check for default/invalid dates (like 0001-01-01)
        if (date.getFullYear() < 1900) {
          return "N/A";
        }
        return date.toLocaleDateString("es-ES", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        });
      } catch {
        return value;
      }
    }
    return value;
  };

  // DeUna section handlers (now for vehiculos by placa)
  const handleGetVehiculo = async () => {
    if (!placaInput.trim()) {
      setVehiculoError("Por favor ingrese una placa de vehículo");
      return;
    }

    setLoadingVehiculo(true);
    setVehiculoError(null);
    setVehiculoData(null);

    try {
      const result = await roldanApi.getVehiculoSOAP(
        mantisBusinessId,
        token,
        placaInput.trim().toUpperCase()
      );

      if (result.success) {
        setVehiculoData(result.data);
      } else {
        setVehiculoError(result.message || "No se pudo obtener el vehículo");
      }
    } catch (error) {
      setVehiculoError(
        error.message ||
          "Error al obtener el vehículo. Por favor intente nuevamente."
      );
    } finally {
      setLoadingVehiculo(false);
    }
  };

  const renderView = () => {
    if (showRemesaForm) {
      return (
        <RemesaForm
          mantisBusinessId={mantisBusinessId}
          mantisBusinessUserId={userId || mantisBusinessUser?._id}
          token={token}
          remesaId={editingRemesaId}
          preManifiestoId={window.currentPreManifiestoIdForRemesa || null}
          onClose={handleCloseForm}
          onSuccess={handleCloseForm}
        />
      );
    }

    if (showPreManifiestoForm) {
      return (
        <PreManifiestoForm
          mantisBusinessId={mantisBusinessId}
          mantisBusinessUserId={userId || mantisBusinessUser?._id}
          token={token}
          preManifiestoId={editingPreManifiestoId}
          onClose={handleClosePreManifiestoForm}
          onSuccess={handleClosePreManifiestoForm}
        />
      );
    }

    if (selectedRemesaId && currentView === "remesas") {
      return (
        <RemesaDetail
          mantisBusinessId={mantisBusinessId}
          mantisBusinessUserId={userId || mantisBusinessUser?._id}
          token={token}
          remesaId={selectedRemesaId}
          onBack={() => setSelectedRemesaId(null)}
          onEdit={handleEditRemesa}
          onRefresh={handleRefresh}
          onPreManifiestoSelect={handlePreManifiestoSelect}
          user={user}
        />
      );
    }

    if (selectedPreManifiestoId && currentView === "pre-manifiestos") {
      return (
        <PreManifiestoDetail
          mantisBusinessId={mantisBusinessId}
          mantisBusinessUserId={userId || mantisBusinessUser?._id}
          token={token}
          preManifiestoId={selectedPreManifiestoId}
          onBack={() => setSelectedPreManifiestoId(null)}
          onRefresh={handleRefresh}
          onCreateRemesa={() => handleCreateRemesa(selectedPreManifiestoId)}
          onManifiestoCreated={(manifiestoId) => {
            setSelectedManifiestoId(manifiestoId);
            setCurrentView("manifiestos");
            setSelectedPreManifiestoId(null);
          }}
          isLogisticsDemo={isLogisticsDemo}
        />
      );
    }

    if (selectedManifiestoId && currentView === "manifiestos") {
      return (
        <ManifiestoDetail
          mantisBusinessId={mantisBusinessId}
          mantisBusinessUserId={userId || mantisBusinessUser?._id}
          token={token}
          manifiestoId={selectedManifiestoId}
          onBack={() => setSelectedManifiestoId(null)}
          onRefresh={handleRefresh}
        />
      );
    }

    switch (currentView) {
      case "panel-control":
        return (
          <PanelControl
            mantisBusinessId={mantisBusinessId}
            mantisBusinessUserId={userId || mantisBusinessUser?._id}
            token={token}
            onRemesaSelect={handleRemesaSelect}
            onPreManifiestoSelect={handlePreManifiestoSelect}
            onManifiestoSelect={handleManifiestoSelect}
            onCreateRemesa={handleCreateRemesa}
            refreshTrigger={refreshTrigger}
            isLogisticsDemo={isLogisticsDemo}
          />
        );
      case "dashboard":
        if (!isAdmin) {
          return (
            <div className="access-denied">
              <AlertTriangle size={48} />
              <p>
                Acceso restringido. Solo administradores pueden ver el
                Dashboard.
              </p>
            </div>
          );
        }
        return (
          <Dashboard
            mantisBusinessId={mantisBusinessId}
            token={token}
            refreshTrigger={refreshTrigger}
          />
        );
      case "remesas":
        return (
          <RemesaList
            mantisBusinessId={mantisBusinessId}
            mantisBusinessUserId={userId || mantisBusinessUser?._id}
            token={token}
            onRemesaSelect={handleRemesaSelect}
            onCreateRemesa={handleCreateRemesa}
            onEditRemesa={handleEditRemesa}
            onPreManifiestoSelect={handlePreManifiestoSelect}
            refreshTrigger={refreshTrigger}
          />
        );
      case "pre-manifiestos":
        return (
          <PreManifiestoList
            mantisBusinessId={mantisBusinessId}
            mantisBusinessUserId={userId || mantisBusinessUser?._id}
            token={token}
            onPreManifiestoSelect={handlePreManifiestoSelect}
            onCreatePreManifiesto={handleCreatePreManifiesto}
            refreshTrigger={refreshTrigger}
          />
        );
      case "manifiestos":
        return (
          <ManifiestoList
            mantisBusinessId={mantisBusinessId}
            mantisBusinessUserId={userId || mantisBusinessUser?._id}
            token={token}
            refreshTrigger={refreshTrigger}
            onManifiestoSelect={handleManifiestoSelect}
          />
        );
      case "alertas":
        return (
          <AlertasList
            mantisBusinessId={mantisBusinessId}
            mantisBusinessUserId={userId || mantisBusinessUser?._id}
            token={token}
            onRemesaSelect={handleRemesaSelect}
            refreshTrigger={refreshTrigger}
          />
        );
      case "de-una":
        return (
          <div className="de-una-section">
            <div className="de-una-header">
              <h2>DeUna</h2>
              <p>Obtener información de vehículo por placa</p>
            </div>

            <div className="de-una-form">
              <div className="form-group">
                <label htmlFor="placa-input">Placa del Vehículo</label>
                <div className="input-group">
                  <input
                    id="placa-input"
                    type="text"
                    value={placaInput}
                    onChange={(e) =>
                      setPlacaInput(e.target.value.toUpperCase())
                    }
                    placeholder="Ingrese la placa del vehículo (ej: ABC123)"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleGetVehiculo();
                      }
                    }}
                  />
                  <button
                    onClick={handleGetVehiculo}
                    disabled={loadingVehiculo}
                    className="btn-obtener"
                  >
                    {loadingVehiculo ? "Cargando..." : "Obtener Vehículo"}
                  </button>
                </div>
              </div>
            </div>

            {vehiculoError && (
              <div className="de-una-error">
                <AlertTriangle size={20} />
                <span>{vehiculoError}</span>
              </div>
            )}

            {vehiculoData && (
              <div className="de-una-result">
                <h3>Información del Vehículo</h3>
                <div className="despacho-data-list">
                  {Object.entries(vehiculoData).map(([key, value]) => {
                    const formattedValue = formatValue(value);
                    const isNull = value === null || value === undefined;
                    return (
                      <div key={key} className="data-item">
                        <span className="data-key">{formatKey(key)}:</span>
                        <span
                          className={`data-value ${isNull ? "value-null" : ""}`}
                        >
                          {formattedValue}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      default:
        return (
          <PanelControl
            mantisBusinessId={mantisBusinessId}
            mantisBusinessUserId={userId || mantisBusinessUser?._id}
            token={token}
            onRemesaSelect={handleRemesaSelect}
            onPreManifiestoSelect={handlePreManifiestoSelect}
            onManifiestoSelect={handleManifiestoSelect}
            onCreateRemesa={handleCreateRemesa}
            refreshTrigger={refreshTrigger}
            isLogisticsDemo={isLogisticsDemo}
          />
        );
    }
  };

  return (
    <div className="roldan-dashboard">
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          className={`mobile-menu-overlay ${mobileMenuOpen ? "active" : ""}`}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <div className={`roldan-sidebar ${mobileMenuOpen ? "mobile-open" : ""}`}>
        <div className="sidebar-header">
          <div className="sidebar-header-content">
            <h2>Roldan</h2>
            <p>Gestión Logística</p>
          </div>
          <button
            className="mobile-menu-close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Cerrar menú"
          >
            <X size={24} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${
              currentView === "panel-control" ? "active" : ""
            }`}
            onClick={() => handleViewChange("panel-control")}
          >
            <LayoutDashboard size={20} />
            <span>Panel de Control</span>
          </button>

          {isAdmin && (
            <button
              className={`nav-item ${
                currentView === "dashboard" ? "active" : ""
              }`}
              onClick={() => handleViewChange("dashboard")}
            >
              <BarChart3 size={20} />
              <span>Dashboard</span>
            </button>
          )}

          <button
            className={`nav-item ${currentView === "remesas" ? "active" : ""}`}
            onClick={() => handleViewChange("remesas")}
          >
            <Package size={20} />
            <span>Remesas</span>
          </button>

          <button
            className={`nav-item ${
              currentView === "pre-manifiestos" ? "active" : ""
            }`}
            onClick={() => handleViewChange("pre-manifiestos")}
          >
            <Truck size={20} />
            <span>Pre-Manifiestos</span>
          </button>

          <button
            className={`nav-item ${
              currentView === "manifiestos" ? "active" : ""
            }`}
            onClick={() => handleViewChange("manifiestos")}
          >
            <FileText size={20} />
            <span>Manifiestos</span>
          </button>

          {/* <button
            className={`nav-item ${currentView === "alertas" ? "active" : ""}`}
            onClick={() => handleViewChange("alertas")}
          >
            <AlertTriangle size={20} />
            <span>Alertas</span>
          </button>

          <button
            className={`nav-item ${currentView === "de-una" ? "active" : ""}`}
            onClick={() => handleViewChange("de-una")}
          >
            <Search size={20} />
            <span>DeUna</span>
          </button> */}
        </nav>

        {currentView === "panel-control" && (
          <button className="create-remesa-btn" onClick={handleCreateRemesa}>
            <Plus size={18} />
            <span>Nueva Remesa</span>
          </button>
        )}
      </div>

      <div className={`roldan-content ${isMobileDeviceType ? 'mobile-device' : ''}`}>
        {/* Mobile menu button */}
        <button
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Abrir menú"
        >
          <Menu size={24} />
        </button>

        <motion.div
          key={currentView}
          initial={{opacity: 0, x: 20}}
          animate={{opacity: 1, x: 0}}
          exit={{opacity: 0, x: -20}}
          transition={{duration: 0.2}}
        >
          {renderView()}
        </motion.div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  const authState = selectAuthState(state);
  return {
    mantisBusinessUser: authState?.mantisBusinessUser,
  };
};

export default connect(mapStateToProps)(RoldanDashboard);
